{/* ==============================================
 * ImageAndFileViewer.jsx
 * Contains ImageViewer and FileViewer components for modal display.
 * ============================================== */}
 import React, { useState } from 'react';
 import ReactDOM from 'react-dom';
 import { X, Copy, Check, FileText, Code } from 'lucide-react';
 
 export const ImageViewer = React.memo(({ src, alt, onClose }) => {
   const portalRoot = document.getElementById('portal-root');
   if (!portalRoot) return null;
 
   return ReactDOM.createPortal(
     <div className="fixed inset-0 z-[5200] flex items-center justify-center">
       <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
       <div className="relative max-w-[90vw] max-h-[90vh] overflow-auto custom-scrollbar">
         <img
           src={src}
           alt={alt}
           className="w-auto h-auto max-w-full max-h-[90vh] rounded-lg"
         />
         <button
           onClick={onClose}
           className="absolute top-4 right-4 p-2 rounded-full bg-surface/80 border border-border-color hover:bg-surface-dark/80 backdrop-blur-sm transition-colors"
         >
           <X size={20} className="text-on-surface" />
         </button>
       </div>
     </div>,
     portalRoot
   );
 });
 
 export const FileViewer = React.memo(({ file, onClose }) => {
   const [copied, setCopied] = useState(false);
   const portalRoot = document.getElementById('portal-root');
   if (!portalRoot) return null;
 
   const handleCopy = async () => {
     try {
       await navigator.clipboard.writeText(file.content);
       setCopied(true);
       setTimeout(() => setCopied(false), 2000);
     } catch (error) {
       console.error('Failed to copy file content:', error);
     }
   };
 
   return ReactDOM.createPortal(
     <div className="fixed inset-0 z-[5200] flex items-center justify-center">
       <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
       <div className="relative max-w-4xl w-full bg-surface rounded-xl shadow-xl overflow-hidden">
         <div className="flex items-center justify-between px-4 py-3 border-b border-border-color">
           <div className="flex items-center gap-3">
             <div className="text-on-surface-dim">
               {/* TODO: This icon logic could be expanded like in FileAttachmentPreview.getPreviewIcon */}
               {file.type?.startsWith('code/') || file.type?.startsWith('text/') ? <Code size={18} /> : <FileText size={18} />}
             </div>
             <h3 className="text-base font-medium text-on-surface">{file.name}</h3>
           </div>
           <div className="flex items-center gap-2">
             {file.content && (
               <button
                 onClick={handleCopy}
                 className="p-2 hover:bg-surface-dark rounded-lg transition-colors"
                 title={copied ? 'Copied!' : 'Copy content'}
               >
                 {copied ? (
                   <Check size={18} className="text-success" />
                 ) : (
                   <Copy size={18} className="text-on-surface-dim" />
                 )}
               </button>
             )}
             <button
               onClick={onClose}
               className="p-2 hover:bg-surface-dark rounded-lg transition-colors"
             >
               <X size={18} className="text-on-surface-dim" />
             </button>
           </div>
         </div>
         <div className="p-6 max-h-[80vh] overflow-auto">
           <div className="text-sm text-on-surface mb-2">
             <p><strong>File Name:</strong> {file.name}</p>
             <p><strong>File Size:</strong> {(file.size / 1024).toFixed(1)} KB</p>
             {/* Consider adding file.type here as well for more info */}
           </div>
           {file.content && ( /* This assumes file.content is displayable text */
             <pre className="whitespace-pre-wrap text-sm font-mono text-on-surface bg-surface-dark/50 p-4 rounded-lg mt-4">
               {file.content}
             </pre>
           )}
         </div>
       </div>
     </div>,
     portalRoot
   );
 });
 
 export default {
   ImageViewer,
   FileViewer
 };